import React from "react"

import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

import Filter from "shared/search/containers/Filter"

import EnumFilter from "app/static/frontend/search-new/components/Filter/EnumFilter/EnumFilter"
import MultiFilter from "app/static/frontend/search-new/components/Filter/MultiFilter/MultiFilter"
import ToggleFilter from "app/static/frontend/search-new/components/Filter/BooleanFilter/BooleanFilter"
import SearchifySelectFilter from "app/static/frontend/search-new/components/Filter/SearchifySelectFilter/SearchifySelectFilter"
import { SearchFilter } from "app/static/frontend/search-new/components/SearchComponents/SearchFilter"

import { IssueManagementFilterableListView } from "shared/search/containers/FilterableListView/FilterableListViews"
import { IssueManagementSelect } from "app/static/frontend/selects/containers/PieSelect"

export const enumOrderDescendingSort = (a, b) => {
    if (a.order && b.order) return b.order - a.order
    else return a.order ? -1 : 1 // prioritize items with a defined order
}

export class GenericFilter extends React.Component {
    static defaultProps = {
        // pass filterKey in so that datePickerGenericFilter can serialize value based on the filterKey
        serialize: (value, filterKey) => value,
        deserialize: (value) => value,
        serializationKey: "generic",
    }

    FilterComponent = isFeatureEnabled("ff_filter_refactor") ? SearchFilter : Filter
    render() {
        return (
            <this.FilterComponent
                presentationComponent={this.props.presentationComponent}
                uniqueFilterId={this.props.uniqueFilterId}
                filterKey={this.props.filterKey}
                {...this.props}
            />
        )
    }
}

export class searchifySelectGenericFilter extends React.Component {
    static defaultProps = {
        presentationComponent: SearchifySelectFilter,
        serialize: (value) => {
            // we have to have this serialize function because there is
            // a chance that value will be passed in as an array of addedIds
            // (visualizations, blah)
            if (Array.isArray(value)) {
                return value.length
                    ? JSON.stringify({
                          addedIds: value,
                          deletedIds: [],
                          querysetList: [],
                          searchifyCount: value.length,
                      })
                    : ""
            } else if (typeof value === "object" && (value.addedIds.length || value.querysetList.length)) {
                return JSON.stringify(value)
            } else {
                return ""
            }
        },
        deserialize: (value) => {
            if (!value) {
                return {}
            }

            // If default filters were selected using PieSelects on the webapp, the value will be a
            // string of "id1,id2,id3,id4...". If by Searchify, it'll be in our desired
            // "{ addedIds: [id1, id2, id3], deletedIds: []... }" and JSON parseable.
            // unfortunately, we have to do the parsing here (and not earlier) to accommodate
            // the Select system / because up until this point the system has no clue
            // what format the data should be in.
            let deserializedValue
            let extra
            let val
            try {
                // If this is a searchify with extra props, split out just the searchify for now, so it can be parsed
                ;[val, extra] = value.split("|")
                // this is the primary use case if using our clickable search
                deserializedValue = JSON.parse(val)
                // the following are use cases from URL inputs / default filters
                if (typeof deserializedValue === "number") {
                    deserializedValue = {
                        addedIds: [deserializedValue],
                        deletedIds: [],
                        querysetList: [],
                        searchifyCount: 1,
                    }
                } else if (typeof deserializedValue === "object") {
                    // must accommodate the scenario where addedIds are provided
                    // as strings, not numbers. Malformed data. :(
                    if (deserializedValue.addedIds && deserializedValue.addedIds.length) {
                        deserializedValue["addedIds"] = deserializedValue.addedIds.map((id) => {
                            if (typeof id === "string") {
                                return isNaN(id) ? id : parseInt(id)
                            }
                            return id
                        })
                    }
                }
            } catch (e) {
                // used in the case of loading default filters from the backend
                const addedIds = value.split(",").map((key) => (isNaN(parseInt(key)) ? key : parseInt(key)))
                deserializedValue = {
                    addedIds,
                    deletedIds: [],
                    querysetList: [],
                    searchifyCount: addedIds.length,
                }
            }

            // If this was a searchify with extra props, add them back in at the end
            return extra ? `${JSON.stringify(deserializedValue)}|${extra}` : deserializedValue
        },
        serializationKey: "select",
    }

    render() {
        return <GenericFilter {...this.props} />
    }
}

export class searchifySelectStringFilter extends searchifySelectGenericFilter {
    static defaultProps = {
        presentationComponent: SearchifySelectFilter,
        serialize: (value) => {
            // we have to have this serialize function because there is
            // a chance that value will be passed in as an array of addedIds
            // (visualizations, blah)
            if (Array.isArray(value)) {
                return value.length
                    ? JSON.stringify({
                          addedIds: value,
                          deletedIds: [],
                          querysetList: [],
                          searchifyCount: value.length,
                      })
                    : ""
            } else if (typeof value === "object" && (value.addedIds.length || value.querysetList.length)) {
                return JSON.stringify(value)
            } else {
                return ""
            }
        },
        deserialize: (value) => {
            if (!value) {
                return {}
            }

            let deserializedValue = JSON.parse(value)
            if (typeof deserializedValue === "number") {
                deserializedValue = {
                    addedIds: [deserializedValue],
                    deletedIds: [],
                    querysetList: [],
                    searchifyCount: 1,
                }
            } else if (typeof deserializedValue === "object") {
                if (deserializedValue.addedIds && deserializedValue.addedIds.length) {
                    deserializedValue["addedIds"] = deserializedValue.addedIds.map((id) => id)
                }
            }

            return deserializedValue
        },
        serializationKey: "select",
    }

    render() {
        return <GenericFilter {...this.props} />
    }
}

export class toggleGenericFilter extends React.Component {
    static defaultProps = {
        presentationComponent: ToggleFilter,
        serialize: (value) => (value != null ? value.toString() : ""),
        deserialize: (value) => (value ? value === "true" : null),
        serializationKey: "toggle",
    }

    render() {
        return <GenericFilter {...this.props} />
    }
}

export class enumGenericFilter extends React.Component {
    static defaultProps = {
        presentationComponent: EnumFilter,
        serialize: (value) => (value ? value.join(",") : ""),
        // if it's a string that represents a number, parse it into an int!
        // otherwise, it's a string value.
        deserialize: (value) => (value ? value.split(",").map((key) => (isNaN(key) ? key : parseInt(key))) : []),
        serializationKey: "enum",
    }

    render() {
        return <GenericFilter {...this.props} />
    }
}

export class enumStringGenericFilter extends React.Component {
    static defaultProps = {
        presentationComponent: EnumFilter,
        serialize: (value) => (value ? value.join(",") : ""),
        deserialize: (value) => (value ? value.split(",").map((key) => key) : []),
        serializationKey: "enum",
    }

    render() {
        return <GenericFilter {...this.props} />
    }
}

export class multiGenericFilter extends React.Component {
    static defaultProps = {
        presentationComponent: MultiFilter,
        filterKey: "",
    }

    render() {
        return <GenericFilter {...this.props} />
    }
}

export const createSelectToggleMultiFilter = (args) =>
    class SelectToggleMultiFilter extends React.Component {
        static get defaultProps() {
            const selectId = `${args.uniqueFilterId}MultiSelect`
            const toggleId = `${args.uniqueFilterId}MultiToggle`
            const enumId = `${args.uniqueFilterId}Enum`

            const select = class SelectSubfilter extends React.Component {
                static defaultProps = {
                    filterComponent: searchifySelectGenericFilter,
                    uniqueFilterId: selectId,
                    filterKey: "",
                    dataTypeLabel: args.dataTypeLabel,
                    enableSelectAllActionButtonSearchify: args.enableSelectAllActionButtonSearchify,
                    maxSearchifySelections: args.maxSearchifySelections,
                    searchifyFLV: args.getSearchifyFLV(), // necessary to avoid circular imports
                    searchifyPermanentFilter: args.searchifyPermanentFilter,
                    selectComponent: args.selectComponent,
                }

                render() {
                    const FilterComponent = this.props.filterComponent
                    return <FilterComponent {...this.props} />
                }
            }

            const toggle = class ToggleSubfilter extends React.Component {
                static defaultProps = {
                    filterComponent: toggleGenericFilter,
                    uniqueFilterId: toggleId,
                    filterKey: "",
                    trueText: "ANY",
                    falseText: "ALL",
                    initialValue: "true",
                    isNullable: false,
                }

                render() {
                    const FilterComponent = this.props.filterComponent
                    return <FilterComponent {...this.props} />
                }
            }

            const generateEnum = () => {
                if (args.enumItems) {
                    return class EnumSubFilter extends React.Component {
                        static defaultProps = {
                            filterComponent: enumGenericFilter,
                            uniqueFilterId: enumId,
                            filterKey: "",
                            enumItems: args.enumItems,
                            displayLabel: args.enumLabel,
                            helpText: args.enumHelpText,
                            sortBy: enumOrderDescendingSort,
                        }

                        render() {
                            const FilterComponent = this.props.filterComponent
                            return <FilterComponent {...this.props} />
                        }
                    }
                }
            }

            return {
                ...args,
                filterComponent: multiGenericFilter,
                uniqueFilterId: args.uniqueFilterId,
                displayLabel: args.displayLabel,
                subfilters: [select, toggle, ...(args.enumItems ? [generateEnum()] : [])],
                allFilterKeys: Object.values(args.filterKeyMapping),
                computeFilterKeyFromSubfilters: (filters) =>
                    filters.get(toggleId) != null ? args.filterKeyMapping[filters.get(toggleId)] : "",
                // This lets us build the query string from the values in redux
                computeFilterValueFromSubfilters: (filters) => {
                    const selectValue = filters.get(selectId)
                    const enumValue = filters.get(enumId)

                    // If this filter has an enum, concatenate the selectValue with the enumValue. ex: "{added_ids: [1, 2, 3]}|10"
                    if (args.enumItems) {
                        if (selectValue || enumValue) {
                            return `${selectValue || ""}${enumValue ? `|${enumValue}` : ""}`
                        }
                    }
                    // Else, just return the selectValue
                    else {
                        return selectValue
                    }
                },
                // This lets us build the values in the redux store from the query string
                computeSubfilterValuesFromParent: (filterKey, value) => {
                    const [selectValue, enumValue] = value.split("|")
                    return {
                        [toggleId]: Object.keys(args.filterKeyMapping).find(
                            (key) => args.filterKeyMapping[key] === filterKey,
                        ),
                        [selectId]: selectValue,
                        ...(args.enumItems ? { [enumId]: enumValue } : {}),
                    }
                },
                serializationKey: searchifySelectGenericFilter.defaultProps.serializationKey,
            }
        }

        render() {
            const FilterComponent = this.props.filterComponent
            return <FilterComponent {...this.props} />
        }
    }

export const inIssueFilter = createSelectToggleMultiFilter({
    uniqueFilterId: "inIssue",
    displayLabel: "Is in my issues...",
    dataTypeLabel: "Issues",
    getSearchifyFLV: () => IssueManagementFilterableListView,
    selectComponent: IssueManagementSelect,
    filterKeyMapping: {
        true: "in_issue_or",
        false: "in_issue_and",
    },
})

export const notInIssueFilter = createSelectToggleMultiFilter({
    uniqueFilterId: "notInIssue",
    displayLabel: "Is not in my issues...",
    dataTypeLabel: "Issues",
    getSearchifyFLV: () => IssueManagementFilterableListView,
    selectComponent: IssueManagementSelect,
    filterKeyMapping: {
        true: "not_in_issue_or",
        false: "not_in_issue_and",
    },
})
